import { getPrimaryErrorCandidate } from "@utils/primaryErrorCandidate";

export type ApiErrorCode =
  | "DB_CONN_ERROR"
  | "QUERY_ASSEMBLY_ERROR"
  | "QUERY_EXEC_ERROR"
  | "POST_QUERY_PROC_ERROR"
  | "AUTH_ERROR"
  | "BAD_REQUEST"
  | "DW_AT_CAPACITY"
  | "DERIVED_FILTER_TOO_LARGE"
  | "QUERY_VOLUME_EXCEEDED"
  | "DERIVED_FILTER_UNSUPPORTED_GRAIN"
  | "MATRIX_COLUMN_REQUIRED"
  | "QUERY_MEMORY_EXCEEDED"
  | "INVALID_RELATIVE_DATE_RULE"
  | "BASE_DATE_WITH_ANCHOR";

export type ChartApiErrorCategory =
  | "query_structure_error"
  | "query_execution_error"
  | "warehouse_at_capacity"
  | "authentication_error"
  | "request_error"
  | "unknown_error";

export type ChartApiErrorHeuristicSubtype = "undefined_field";

export interface ApiErrorResponse {
  code?: string;
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
  message?: string;
  status?: number;
  data?: {
    code?: string;
    error?: {
      code?: string;
      message?: string;
      details?: unknown;
    };
    message?: string;
    details?: unknown;
    status?: number;
  };
  errors?: Array<{
    code?: string;
    message?: string;
    details?: unknown;
    status?: number;
    data?: {
      code?: string;
      error?: {
        code?: string;
        message?: string;
        details?: unknown;
      };
      message?: string;
      details?: unknown;
      status?: number;
    };
  }>;
}

export interface ApiError {
  code?: string;
  message: string;
  status?: number;
  details?: unknown;
}

export interface NormalizedChartApiError {
  status?: number;
  apiCode?: string;
  category: ChartApiErrorCategory;
  heuristicSubtype?: ChartApiErrorHeuristicSubtype;
  userMessage: string;
  rawMessage: string;
  details?: unknown;
  raw: unknown;
}

const ERROR_MESSAGES: Record<ApiErrorCode, string> = {
  DB_CONN_ERROR: "Falha na conexão com o banco de dados",
  QUERY_ASSEMBLY_ERROR: "A consulta está vazia",
  QUERY_EXEC_ERROR: "Falha na execução da consulta",
  POST_QUERY_PROC_ERROR: "Falha no processamento pós-consulta",
  AUTH_ERROR: "Falha de autenticação",
  BAD_REQUEST: "Requisição inválida",
  DW_AT_CAPACITY: "O banco de dados está processando muitas consultas no momento",
  // Limites do indicador matricial / volume da consulta (bi-engine#449,
  // poc#281): a mensagem diz o que fazer, não só que falhou.
  DERIVED_FILTER_TOO_LARGE:
    "O filtro pela coluna do indicador alcança valores demais. Refine os filtros ou use uma dimensão de grão maior.",
  QUERY_VOLUME_EXCEEDED:
    "A consulta excede o limite de linhas para processamento. Refine os filtros ou agrupe os dados.",
  DERIVED_FILTER_UNSUPPORTED_GRAIN:
    "Este indicador de tabela tem grão composto e não pode ser usado como filtro.",
  MATRIX_COLUMN_REQUIRED:
    "O indicador de tabela precisa ser usado por uma de suas colunas.",
  // Estouro do teto de memória do motor. A mensagem nomeia as duas saídas que
  // resolvem na prática — sem elas o usuário vê que falhou e não o que mudar.
  QUERY_MEMORY_EXCEEDED:
    "A consulta pediu mais memória do que o ambiente permite. Reduza o período consultado " +
    "ou troque a agregação das colunas numéricas para Nenhuma.",
  INVALID_RELATIVE_DATE_RULE: "O período deste filtro de data não é válido. Revise o filtro.",
  BASE_DATE_WITH_ANCHOR:
    "Este elemento já tem uma data fixa no filtro de consulta. " +
    "Desmarque a data base do filtro de data ou remova a âncora.",
};

const ERROR_STATUS: Record<ApiErrorCode, number> = {
  DB_CONN_ERROR: 500,
  QUERY_ASSEMBLY_ERROR: 400,
  QUERY_EXEC_ERROR: 400,
  POST_QUERY_PROC_ERROR: 500,
  AUTH_ERROR: 401,
  BAD_REQUEST: 400,
  DW_AT_CAPACITY: 503,
  DERIVED_FILTER_TOO_LARGE: 422,
  QUERY_VOLUME_EXCEEDED: 422,
  DERIVED_FILTER_UNSUPPORTED_GRAIN: 422,
  MATRIX_COLUMN_REQUIRED: 422,
  QUERY_MEMORY_EXCEEDED: 422,
  INVALID_RELATIVE_DATE_RULE: 422,
  BASE_DATE_WITH_ANCHOR: 422,
};

/**
 * Erros em que o usuário resolve refinando a consulta (filtros/grão) — a UI
 * mostra a mensagem específica em vez do aviso genérico de erro de consulta.
 */
const ACTIONABLE_QUERY_LIMIT_CODES: ReadonlySet<string> = new Set([
  "DERIVED_FILTER_TOO_LARGE",
  "QUERY_VOLUME_EXCEEDED",
  "DERIVED_FILTER_UNSUPPORTED_GRAIN",
  "MATRIX_COLUMN_REQUIRED",
  "QUERY_MEMORY_EXCEEDED",
  "INVALID_RELATIVE_DATE_RULE",
  "BASE_DATE_WITH_ANCHOR",
]);

export const isActionableQueryLimitError = (error: Pick<NormalizedChartApiError, "apiCode">): boolean => {
  return !!error.apiCode && ACTIONABLE_QUERY_LIMIT_CODES.has(error.apiCode);
};

const QUERY_STRUCTURE_UNDEFINED_FIELD_MESSAGE =
  "Este gráfico usa um campo que não existe no schema disponível neste ambiente.";

const QUERY_STRUCTURE_UNDEFINED_FIELD_SIGNATURES = ["reference to undefined value"];

const APP_ERROR_WRAPPER_PREFIX = "domain.modules.helpers.exceptions.apperror:";

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const isKnownApiErrorCode = (value: string | undefined): value is ApiErrorCode => {
  if (!value) {
    return false;
  }

  return value in ERROR_MESSAGES;
};

const getString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : undefined;
};

const getNumber = (value: unknown): number | undefined => {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};

const isWrappedAppErrorMessage = (value: string | undefined): boolean => {
  if (!value) {
    return false;
  }

  return value.toLowerCase().startsWith(APP_ERROR_WRAPPER_PREFIX);
};

const getDataPayload = (primaryError: Record<string, unknown>): ApiErrorResponse["data"] | undefined => {
  return primaryError.data as ApiErrorResponse["data"] | undefined;
};

const getNestedMessage = (primaryError: Record<string, unknown>): string | undefined => {
  const dataPayload = getDataPayload(primaryError);
  const dataMessage = getString(dataPayload?.message);
  const dataDetailsMessage =
    getString(dataPayload?.details) ?? getString(dataPayload?.error?.message) ?? getString(dataPayload?.error?.details);

  if (isWrappedAppErrorMessage(dataMessage) && dataDetailsMessage) {
    return dataDetailsMessage;
  }

  return dataMessage ?? dataDetailsMessage;
};

const extractApiCode = (error: unknown): string | undefined => {
  const primaryError = getPrimaryErrorCandidate(error);

  if (!primaryError) {
    return undefined;
  }

  return (
    getString(primaryError.apiCode) ??
    getString(primaryError.code) ??
    getString(getDataPayload(primaryError)?.error?.code) ??
    getString(getDataPayload(primaryError)?.code) ??
    getString((primaryError.error as ApiErrorResponse["error"])?.code)
  );
};

const extractRawMessage = (error: unknown, defaultMessage: string): string => {
  if (typeof error === "string") {
    return error;
  }

  const primaryError = getPrimaryErrorCandidate(error);

  if (!primaryError) {
    return defaultMessage;
  }

  return (
    getString(primaryError.rawMessage) ??
    getNestedMessage(primaryError) ??
    getString(primaryError.message) ??
    getString((primaryError.errors as ApiErrorResponse["errors"])?.[0]?.message) ??
    getString((primaryError.error as ApiErrorResponse["error"])?.message) ??
    defaultMessage
  );
};

const extractDetails = (error: unknown): unknown => {
  const primaryError = getPrimaryErrorCandidate(error);

  if (!primaryError) {
    return undefined;
  }

  return (
    primaryError.details ??
    (primaryError.data as ApiErrorResponse["data"])?.error?.details ??
    (primaryError.error as ApiErrorResponse["error"])?.details ??
    (primaryError.data as ApiErrorResponse["data"])?.details
  );
};

const extractStatus = (error: unknown, apiCode: string | undefined): number | undefined => {
  const primaryError = getPrimaryErrorCandidate(error);

  if (primaryError) {
    const directStatus =
      getNumber(primaryError.status) ?? getNumber((primaryError.data as ApiErrorResponse["data"])?.status);

    if (directStatus !== undefined) {
      return directStatus;
    }
  }

  if (isKnownApiErrorCode(apiCode)) {
    return ERROR_STATUS[apiCode];
  }

  return undefined;
};

const getKnownErrorMessage = (apiCode: string | undefined): string | undefined => {
  if (!isKnownApiErrorCode(apiCode)) {
    return undefined;
  }

  return ERROR_MESSAGES[apiCode];
};

const classifyChartApiError = ({
  apiCode,
  rawMessage,
  defaultMessage,
}: {
  apiCode?: string;
  rawMessage: string;
  defaultMessage: string;
}): {
  category: ChartApiErrorCategory;
  heuristicSubtype: ChartApiErrorHeuristicSubtype | undefined;
  userMessage: string;
} => {
  const normalizedRawMessage = rawMessage.toLowerCase();
  const isUndefinedFieldStructureError =
    apiCode === "QUERY_ASSEMBLY_ERROR" &&
    QUERY_STRUCTURE_UNDEFINED_FIELD_SIGNATURES.some((signature) => normalizedRawMessage.includes(signature));

  if (isUndefinedFieldStructureError) {
    return {
      category: "query_structure_error" as const,
      heuristicSubtype: "undefined_field" as const,
      userMessage: QUERY_STRUCTURE_UNDEFINED_FIELD_MESSAGE,
    };
  }

  if (apiCode === "DW_AT_CAPACITY") {
    return {
      category: "warehouse_at_capacity" as const,
      heuristicSubtype: undefined,
      userMessage: getKnownErrorMessage(apiCode) ?? rawMessage,
    };
  }

  if (apiCode === "QUERY_ASSEMBLY_ERROR" || apiCode === "QUERY_EXEC_ERROR" || apiCode === "POST_QUERY_PROC_ERROR") {
    return {
      category: "query_execution_error" as const,
      heuristicSubtype: undefined,
      userMessage: getKnownErrorMessage(apiCode) ?? rawMessage,
    };
  }

  if (apiCode === "AUTH_ERROR") {
    return {
      category: "authentication_error" as const,
      heuristicSubtype: undefined,
      userMessage: getKnownErrorMessage(apiCode) ?? rawMessage,
    };
  }

  if (apiCode || rawMessage !== defaultMessage) {
    return {
      category: "request_error" as const,
      heuristicSubtype: undefined,
      userMessage: getKnownErrorMessage(apiCode) ?? rawMessage,
    };
  }

  return {
    category: "unknown_error" as const,
    heuristicSubtype: undefined,
    userMessage: defaultMessage,
  };
};

export const isQueryStructureUndefinedFieldError = (error: NormalizedChartApiError): boolean => {
  return error.category === "query_structure_error" && error.heuristicSubtype === "undefined_field";
};

const isWarehouseAtCapacityCategory = (value: unknown): value is NormalizedChartApiError => {
  return isRecord(value) && value.category === "warehouse_at_capacity";
};

// Accepts a raw/unknown error (not just an already-normalized one) because it's
// meant to be called directly from a React Query `retry` callback, which sees
// whatever `queryFn` threw: an already-normalized VersionedChartError
// (useChartDataLoader), an Error wrapping one via `.cause` (useWindowedTableData's
// fetchTableWindow), or a raw unnormalized error.
export function isWarehouseAtCapacityError(error: unknown): boolean {
  if (isWarehouseAtCapacityCategory(error)) {
    return true;
  }

  const cause = error instanceof Error ? error.cause : undefined;
  if (isWarehouseAtCapacityCategory(cause)) {
    return true;
  }

  return normalizeChartApiError(error).category === "warehouse_at_capacity";
}

export function normalizeChartApiError(
  error: unknown,
  defaultMessage = "Erro desconhecido"
): NormalizedChartApiError {
  const apiCode = extractApiCode(error);
  const rawMessage = extractRawMessage(error, defaultMessage);
  const details = extractDetails(error);
  const status = extractStatus(error, apiCode);
  const classification = classifyChartApiError({ apiCode, rawMessage, defaultMessage });

  return {
    status,
    apiCode,
    category: classification.category,
    heuristicSubtype: classification.heuristicSubtype,
    userMessage: classification.userMessage,
    rawMessage,
    details,
    raw: error,
  };
}

export function extractErrorMessage(error: unknown, defaultMessage = "Erro desconhecido"): string {
  return normalizeChartApiError(error, defaultMessage).userMessage;
}

export function extractApiError(error: unknown): ApiError | null {
  if (!error) {
    return null;
  }

  const normalizedError = normalizeChartApiError(error, "Erro na requisição");

  return {
    code: normalizedError.apiCode,
    message: normalizedError.userMessage,
    status: normalizedError.status,
    details: normalizedError.details,
  };
}

export function isApiError(error: unknown): error is ApiErrorResponse {
  if (!isRecord(error)) {
    return false;
  }

  const apiCode = extractApiCode(error);

  if (apiCode !== undefined) {
    return true;
  }

  if (extractStatus(error, apiCode) !== undefined) {
    return true;
  }

  if (!Array.isArray(error.errors) || !error.errors.length) {
    return false;
  }

  const primaryNestedError = error.errors[0];

  if (!isRecord(primaryNestedError)) {
    return false;
  }

  const nestedApiCode = extractApiCode(primaryNestedError);

  return nestedApiCode !== undefined || extractStatus(primaryNestedError, nestedApiCode) !== undefined;
}

export function getErrorStatus(error: unknown): number | undefined {
  return normalizeChartApiError(error, "Erro na requisição").status;
}

// eslint-disable-next-line no-console -- single console.error entry point for structured error logging
export const logError = (module: string, message: string, context?: Record<string, unknown>): void =>
  console.error(`[${module}] ${message}`, context ?? "");
